// firebaseService.js
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from './firebase';
import { chunk } from 'lodash';

export const subscribeToCollection = (collectionName, callback) => {
	const colRef = collection(firestore, collectionName);
	return onSnapshot(colRef, (snapshot) => {
		const data = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		callback(data);
	});
};

export const queryByOwnerId = async ({
	collectionRef,
	constraints = [],
	ownerField = 'ownerId',
	ownerValue,
}) => {
	if (!ownerValue) {
		const q = query(collectionRef, ...[...constraints]);
		const querySnapshot = await getDocs(q);
		return querySnapshot.docs;
	}

	const q = query(collectionRef, ...[...constraints, where(ownerField, '==', ownerValue)]);
	const querySnapshot = await getDocs(q);
	return querySnapshot.docs;
};

export const queryByBrokerGroup = async ({
	collectionRef,
	brokerGroupConstraint = { fieldPath: 'brokerGroupId', optString: 'in' },
	constraints = [],
	brokerGroupIds = [],
}) => {
	const brokerGroupChunks = chunk(brokerGroupIds, 15);
	const seen = new Map();

	if (brokerGroupIds.length === 0) {
		return [];
	}

	await Promise.all(
		brokerGroupChunks.map(async (groupChunk) => {
			const q = query(
				collectionRef,
				...[
					...constraints,
					where(
						brokerGroupConstraint.fieldPath ?? 'brokerGroupId',
						brokerGroupConstraint.optString ?? 'in',
						groupChunk,
					),
				],
			);

			const querySnapshot = await getDocs(q);

			querySnapshot.docs.forEach((doc) => {
				seen.set(doc.id, doc);
			});
		}),
	);

	return Array.from(seen.values());
};

// Whether the user sees every client and deal in their broker groups, not just their own
// (the Firestore rules: canViewGroup, or a principal user).
export const canViewGroup = (user) => Boolean(user?.canViewGroup || user?.principleUser);

/**
 * Clients or deals the user can list under the Firestore rules: their broker groups' when
 * they can view the group, otherwise only the ones they own. `ownerFields` are the fields
 * that make a record theirs (for deals, `ownerDocId` and `clientOwnerDocId`).
 */
export const queryViewableRecords = async ({
	collectionRef,
	constraints = [],
	user,
	ownerFields = ['ownerDocId'],
}) => {
	if (canViewGroup(user)) {
		return queryByBrokerGroup({
			collectionRef,
			constraints,
			brokerGroupIds: user.brokerGroupArray ?? [],
		});
	}

	// Never query without an owner value: queryByOwnerId drops the owner filter when it's
	// falsy.
	if (!user?.userId) return [];

	const docsByField = await Promise.all(
		ownerFields.map((ownerField) =>
			queryByOwnerId({ collectionRef, constraints, ownerField, ownerValue: user.userId }),
		),
	);
	return Array.from(new Map(docsByField.flat().map((d) => [d.id, d])).values());
};
